import _toConsumableArray from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.28.4/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _defineProperty from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.28.4/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { addFetchEndInstrumentationHandler, addFetchInstrumentationHandler, instrumentFetchRequest, parseUrl, getLocationHref, stringMatchesSomePattern, spanToJSON, hasSpansEnabled, setHttpStatus, stripUrlQueryAndFragment, getActiveSpan, startInactiveSpan, SEMANTIC_ATTRIBUTE_SENTRY_OP, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, SentryNonRecordingSpan, getClient, getTraceData } from '@sentry/core';
import { addXhrInstrumentationHandler, addPerformanceInstrumentationHandler, SENTRY_XHR_DATA_KEY } from '@sentry-internal/browser-utils';
import { WINDOW } from '../helpers.js';
import { resourceTimingToSpanAttributes } from './resource-timing.js';

/** Options for Request Instrumentation */

var responseToSpanId = new WeakMap();
var spanIdToEndTimestamp = new Map();
var defaultRequestInstrumentationOptions = {
  traceFetch: true,
  traceXHR: true,
  enableHTTPTimings: true,
  trackFetchStreamPerformance: false
};

/** Registers span creators for xhr and fetch requests  */
function instrumentOutgoingRequests(client, _options) {
  var _defaultRequestInstru = _objectSpread(_objectSpread({}, defaultRequestInstrumentationOptions), _options),
    traceFetch = _defaultRequestInstru.traceFetch,
    traceXHR = _defaultRequestInstru.traceXHR,
    trackFetchStreamPerformance = _defaultRequestInstru.trackFetchStreamPerformance,
    shouldCreateSpanForRequest = _defaultRequestInstru.shouldCreateSpanForRequest,
    enableHTTPTimings = _defaultRequestInstru.enableHTTPTimings,
    tracePropagationTargets = _defaultRequestInstru.tracePropagationTargets,
    onRequestSpanStart = _defaultRequestInstru.onRequestSpanStart;
  var shouldCreateSpan = typeof shouldCreateSpanForRequest === 'function' ? shouldCreateSpanForRequest : function (_) {
    return true;
  };
  var shouldAttachHeadersWithTargets = function shouldAttachHeadersWithTargets(url) {
    return shouldAttachHeaders(url, tracePropagationTargets);
  };
  var spans = {};
  if (traceFetch) {
    // Keeping track of http requests, whose body payloads resolved later than the initial resolved request
    // e.g. streaming using server sent events (SSE)
    client.addEventProcessor(function (event) {
      if (event.type === 'transaction' && event.spans) {
        event.spans.forEach(function (span) {
          if (span.op === 'http.client') {
            var updatedTimestamp = spanIdToEndTimestamp.get(span.span_id);
            if (updatedTimestamp) {
              span.timestamp = updatedTimestamp / 1000;
              spanIdToEndTimestamp.delete(span.span_id);
            }
          }
        });
      }
      return event;
    });
    if (trackFetchStreamPerformance) {
      addFetchEndInstrumentationHandler(function (handlerData) {
        if (handlerData.response) {
          var span = responseToSpanId.get(handlerData.response);
          if (span && handlerData.endTimestamp) {
            spanIdToEndTimestamp.set(span, handlerData.endTimestamp);
          }
        }
      });
    }
    addFetchInstrumentationHandler(function (handlerData) {
      var createdSpan = instrumentFetchRequest(handlerData, shouldCreateSpan, shouldAttachHeadersWithTargets, spans);
      if (handlerData.response && handlerData.fetchData.__span) {
        responseToSpanId.set(handlerData.response, handlerData.fetchData.__span);
      }

      // We cannot use `window.location` in the generic fetch instrumentation,
      // but we need it for reliable `server.address` attribute.
      // so we extend this in here
      if (createdSpan) {
        var fullUrl = getFullURL(handlerData.fetchData.url);
        var host = fullUrl ? parseUrl(fullUrl).host : undefined;
        createdSpan.setAttributes({
          'http.url': fullUrl,
          'server.address': host
        });
        if (enableHTTPTimings) {
          addHTTPTimings(createdSpan);
        }
        onRequestSpanStart === null || onRequestSpanStart === void 0 || onRequestSpanStart(createdSpan, {
          headers: handlerData.headers
        });
      }
    });
  }
  if (traceXHR) {
    addXhrInstrumentationHandler(function (handlerData) {
      var createdSpan = xhrCallback(handlerData, shouldCreateSpan, shouldAttachHeadersWithTargets, spans);
      if (createdSpan) {
        if (enableHTTPTimings) {
          addHTTPTimings(createdSpan);
        }
        var headers;
        try {
          var _handlerData$xhr$__se;
          headers = new Headers((_handlerData$xhr$__se = handlerData.xhr.__sentry_xhr_v3__) === null || _handlerData$xhr$__se === void 0 ? void 0 : _handlerData$xhr$__se.request_headers);
        } catch (_unused) {
          // noop
        }
        onRequestSpanStart === null || onRequestSpanStart === void 0 || onRequestSpanStart(createdSpan, {
          headers: headers
        });
      }
    });
  }
}
function isPerformanceResourceTiming(entry) {
  return entry.entryType === 'resource' && 'initiatorType' in entry && typeof entry.nextHopProtocol === 'string' && (entry.initiatorType === 'fetch' || entry.initiatorType === 'xmlhttprequest');
}

/**
 * Creates a temporary observer to listen to the next fetch/xhr resourcing timings,
 * so that when timings hit their per-browser limit they don't need to be removed.
 *
 * @param span A span that has yet to be finished, must contain `url` on data.
 */
function addHTTPTimings(span) {
  var url = spanToJSON(span).data.url;
  if (!url || typeof url !== 'string') {
    return;
  }
  var cleanup = addPerformanceInstrumentationHandler('resource', function (_ref) {
    var entries = _ref.entries;
    entries.forEach(function (entry) {
      if (isPerformanceResourceTiming(entry) && entry.name.endsWith(url)) {
        var spanAttributes = resourceTimingToSpanAttributes(entry);
        spanAttributes.forEach(function (attributeArray) {
          return span.setAttribute.apply(span, _toConsumableArray(attributeArray));
        });
        // In the next tick, clean this handler up
        // We have to wait here because otherwise this cleans itself up before it is fully done
        setTimeout(cleanup);
      }
    });
  });
}

/**
 * A function that determines whether to attach tracing headers to a request.
 * We only export this function for testing purposes.
 */
function shouldAttachHeaders(targetUrl, tracePropagationTargets) {
  // window.location.href not being defined is an edge case in the browser but we need to handle it.
  // Potentially dangerous situations where it may not be defined: Browser Extensions, Web Workers, patching of the location obj
  var href = getLocationHref();
  if (!href) {
    // If there is no window.location.origin, we default to only attaching tracing headers to relative requests, i.e. ones that start with `/`
    // BIG DISCLAIMER: Users can call URLs with a double slash (fetch("//example.com/api")), this is a shorthand for "send to the same protocol",
    // so we need a to exclude those requests, because they might be cross origin.
    var isRelativeSameOriginRequest = !!targetUrl.match(/^\/(?!\/)/);
    if (!tracePropagationTargets) {
      return isRelativeSameOriginRequest;
    } else {
      return stringMatchesSomePattern(targetUrl, tracePropagationTargets);
    }
  } else {
    var resolvedUrl;
    var currentOrigin;

    // URL parsing may fail, we default to not attaching trace headers in that case.
    try {
      resolvedUrl = new URL(targetUrl, href);
      currentOrigin = new URL(href).origin;
    } catch (_unused2) {
      return false;
    }
    var isSameOriginRequest = resolvedUrl.origin === currentOrigin;
    if (!tracePropagationTargets) {
      return isSameOriginRequest;
    } else {
      return stringMatchesSomePattern(resolvedUrl.toString(), tracePropagationTargets) || isSameOriginRequest && stringMatchesSomePattern(resolvedUrl.pathname, tracePropagationTargets);
    }
  }
}

/**
 * Create and track xhr request spans
 *
 * @returns Span if a span was created, otherwise void.
 */
function xhrCallback(handlerData, shouldCreateSpan, shouldAttachHeaders, spans) {
  var xhr = handlerData.xhr;
  var sentryXhrData = xhr === null || xhr === void 0 ? void 0 : xhr[SENTRY_XHR_DATA_KEY];
  if (!xhr || xhr.__sentry_own_request__ || !sentryXhrData) {
    return undefined;
  }
  var url = sentryXhrData.url,
    method = sentryXhrData.method;
  var shouldCreateSpanResult = hasSpansEnabled() && shouldCreateSpan(url);

  // check first if the request has finished and is tracked by an existing span which should now end
  if (handlerData.endTimestamp && shouldCreateSpanResult) {
    var spanId = xhr.__sentry_xhr_span_id__;
    if (!spanId) return;
    var _span = spans[spanId];
    if (_span && sentryXhrData.status_code !== undefined) {
      setHttpStatus(_span, sentryXhrData.status_code);
      _span.end();

      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete spans[spanId];
    }
    return undefined;
  }
  var fullUrl = getFullURL(url);
  var parsedUrl = fullUrl ? parseUrl(fullUrl) : parseUrl(url);
  var urlForSpanName = stripUrlQueryAndFragment(url);
  var hasParent = !!getActiveSpan();
  var span = shouldCreateSpanResult && hasParent ? startInactiveSpan({
    name: "".concat(method, " ").concat(urlForSpanName),
    attributes: _objectSpread(_objectSpread(_defineProperty(_defineProperty({
      url: url,
      type: 'xhr',
      'http.method': method,
      'http.url': fullUrl,
      'server.address': parsedUrl === null || parsedUrl === void 0 ? void 0 : parsedUrl.host
    }, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, 'auto.http.browser'), SEMANTIC_ATTRIBUTE_SENTRY_OP, 'http.client'), (parsedUrl === null || parsedUrl === void 0 ? void 0 : parsedUrl.search) && {
      'http.query': parsedUrl === null || parsedUrl === void 0 ? void 0 : parsedUrl.search
    }), (parsedUrl === null || parsedUrl === void 0 ? void 0 : parsedUrl.hash) && {
      'http.fragment': parsedUrl === null || parsedUrl === void 0 ? void 0 : parsedUrl.hash
    })
  }) : new SentryNonRecordingSpan();
  xhr.__sentry_xhr_span_id__ = span.spanContext().spanId;
  spans[xhr.__sentry_xhr_span_id__] = span;
  if (shouldAttachHeaders(url)) {
    addTracingHeadersToXhrRequest(xhr,
    // If performance is disabled (TWP) or there's no active root span (pageload/navigation/interaction),
    // we do not want to use the span as base for the trace headers,
    // which means that the headers will be generated from the scope and the sampling decision is deferred
    hasSpansEnabled() && hasParent ? span : undefined);
  }
  var client = getClient();
  if (client) {
    client.emit('beforeOutgoingRequestSpan', span, handlerData);
  }
  return span;
}
function addTracingHeadersToXhrRequest(xhr, span) {
  var _getTraceData = getTraceData({
      span: span
    }),
    sentryTrace = _getTraceData['sentry-trace'],
    baggage = _getTraceData.baggage;
  if (sentryTrace) {
    setHeaderOnXhr(xhr, sentryTrace, baggage);
  }
}
function setHeaderOnXhr(xhr, sentryTraceHeader, sentryBaggageHeader) {
  var _xhr$__sentry_xhr_v3_;
  var originalHeaders = (_xhr$__sentry_xhr_v3_ = xhr.__sentry_xhr_v3__) === null || _xhr$__sentry_xhr_v3_ === void 0 ? void 0 : _xhr$__sentry_xhr_v3_.request_headers;
  if (originalHeaders !== null && originalHeaders !== void 0 && originalHeaders['sentry-trace']) {
    // bail if a sentry-trace header is already set
    return;
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    xhr.setRequestHeader('sentry-trace', sentryTraceHeader);
    if (sentryBaggageHeader) {
      // only add our headers if
      // - no pre-existing baggage header exists
      // - or it is set and doesn't yet contain sentry values
      var originalBaggageHeader = originalHeaders === null || originalHeaders === void 0 ? void 0 : originalHeaders['baggage'];
      if (!originalBaggageHeader || !baggageHeaderHasSentryValues(originalBaggageHeader)) {
        // From MDN: "If this method is called several times with the same header, the values are merged into one single request header."
        // We can therefore simply set a baggage header without checking what was there before
        // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/setRequestHeader
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        xhr.setRequestHeader('baggage', sentryBaggageHeader);
      }
    }
  } catch (_unused3) {
    // Error: InvalidStateError: Failed to execute 'setRequestHeader' on 'XMLHttpRequest': The object's state must be OPENED.
  }
}
function baggageHeaderHasSentryValues(baggageHeader) {
  return baggageHeader.split(',').some(function (value) {
    return value.trim().startsWith('sentry-');
  });
}
function getFullURL(url) {
  try {
    // By adding a base URL to new URL(), this will also work for relative urls
    // If `url` is a full URL, the base URL is ignored anyhow
    var parsed = new URL(url, WINDOW.location.origin);
    return parsed.href;
  } catch (_unused4) {
    return undefined;
  }
}
export { defaultRequestInstrumentationOptions, instrumentOutgoingRequests, shouldAttachHeaders, xhrCallback };